import { useState, useEffect } from 'react'
import { useBookmarkStore } from '../store/bookmarkStore'
import { Button } from './Button'
import { Bookmark } from '../types/bookmark'
import { X } from 'lucide-react'
import { Dialog } from './Dialog'

interface BookmarkFormProps {
  bookmark?: Bookmark
  onClose: () => void
}

// 添加API基础URL配置
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export function BookmarkForm({ bookmark, onClose }: BookmarkFormProps) {
  const { categories, addBookmark, updateBookmark } = useBookmarkStore()
  const [formData, setFormData] = useState({
    title: bookmark?.title || '',
    url: bookmark?.url || '',
    description: bookmark?.description || '',
    categoryId: bookmark?.categoryId || '',
    tags: bookmark?.tags || [],
    favicon: bookmark?.favicon || '',
  })
  const [newTag, setNewTag] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // 添加防抖函数
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout
    return (...args: any[]) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }

  // 获取URL元数据
  const fetchMetadata = async (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }

    try {
      setIsLoading(true)
      const response = await fetch(`${API_BASE_URL}/api/preview-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      if (data.error) {
        throw new Error(data.error)
      }
      
      setFormData(prev => ({
        ...prev,
        title: prev.title || data.title,
        description: prev.description || data.description,
        favicon: prev.favicon || data.favicon || '',
      }))
    } catch (error) {
      console.error('获取网页信息失败:', error)
      // 可以在这里添加用户提示
    } finally {
      setIsLoading(false)
    }
  }

  // 使用防抖处理URL变化
  const debouncedFetchMetadata = debounce(fetchMetadata, 500)

  // 处理URL变化
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value.trim()
    setFormData(prev => ({ ...prev, url: newUrl }))
    
    if (newUrl && !formData.title) {
      debouncedFetchMetadata(newUrl)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const bookmarkData = {
      ...formData,
      isFavorite: bookmark?.isFavorite || false,
    }
    if (bookmark) {
      updateBookmark(bookmark.id, bookmarkData)
    } else {
      addBookmark(bookmarkData)
    }
    onClose()
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      })
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    })
  }

  return (
    <Dialog title={bookmark ? '编辑书签' : '添加书签'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="url" className="block text-sm font-medium text-gray-700">
            网址
          </label>
          <input
            type="url"
            id="url"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.url}
            onChange={handleUrlChange}
          />
        </div>

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            标题 {isLoading && <span className="text-gray-400 text-xs ml-2">获取中...</span>}
          </label>
          <input
            type="text"
            id="title"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            描述
          </label>
          <textarea
            id="description"
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
            分类
          </label>
          <select
            id="category"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.categoryId}
            onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
          >
            <option value="">无分类</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
            标签
          </label>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                id="tags"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
                placeholder="输入标签后按回车添加"
              />
              <Button
                type="button"
                onClick={handleAddTag}
                className="shrink-0"
              >
                添加标签
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="outline" onClick={onClose} type="button">
            取消
          </Button>
          <Button type="submit">
            {bookmark ? '更新' : '添加'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
} 